import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { EmptyState } from '../../../shared/EmptyState';
import { RetryableError } from '../../../shared/RetryableError';
import { UpgradeRefusalNotice } from '../../../shared/UpgradeRefusalNotice';
import { clearSubscriptionRestrictionNotice, upgradeRequiredFromError, type SubscriptionRestriction } from '../../../shared/subscriptionRestriction';
import { ExportOptionsPanel } from '../components/ExportOptionsPanel';
import { ExportPreviewView } from '../components/ExportPreviewView';
import { fixMappingUrl, previewIsExportable } from '../exportPreviewLogic';
import { StatusBadge } from '../components/StatusBadge';
import { saveDownloadedExport } from '../downloadFile';
import { ExportErrorCode, ExportValidationError, exportErrorMessage, hasCode, isForbidden } from '../exportErrors';
import { formatDate, formatDateTime, periodLabel, unmappedLabel, unmappedReasonLabel } from '../exportFormat';
import { NO_OVERRIDES, requestOptions, useExportSettings, type OptionOverrides } from '../exportOptionState';
import { describeNetPay, describeWage } from '../exportOptions';
import {
  exportModeLabels, exportStatusLabel, type EligiblePayrolls, type ExportDetail, type ExportMode, type ExportPreview, type ExportSummary, type MappingReadiness, type PayScheduleOption,
} from '../exportTypes';
import { accountingGateway } from '../index';
import { formatCents } from '../money';
import { useExportCapabilities } from '../useExportCapabilities';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

type ListState = { kind: 'loading' } | { kind: 'ready'; data: EligiblePayrolls } | { kind: 'forbidden' } | { kind: 'error'; message: string };
type PreviewState = { kind: 'idle' } | { kind: 'loading' } | { kind: 'ready'; data: ExportPreview } | { kind: 'error'; message: string };
type ResultState =
  | { kind: 'generated'; export: ExportDetail; duplicate: boolean; fileName: string | null; downloadError: string | null }
  | { kind: 'blocked'; message: string }
  | { kind: 'error'; message: string };

const MODE_COPY: Record<ExportMode, string> = {
  Compound: 'One combined entry for the whole payroll, with a single summarized net pay credit to Payroll Clearing.',
  PerEmployee: 'Each employee\'s lines kept apart, with one net pay credit per employee to Payroll Clearing.',
};

/** Why Export CSV cannot be pressed right now, in words (so a disabled button is never a mystery); null when it can. */
function blockedReason(args: {
  hasRun: boolean; preview: ExportPreview | null; canGenerate: boolean; writeDenied: boolean; busy: boolean; existing: ExportSummary | null;
}): string | null {
  if (!args.hasRun) return 'Choose a finalized payroll first.';
  if (!args.canGenerate || args.writeDenied) return 'You can preview an export, but generating one needs a permission you do not have. Ask a company administrator.';
  if (args.existing) {
    switch (args.existing.status) {
      case 'ReversalRequired': return 'This payroll changed after it was exported. Generate a reversal in History before exporting again.';
      case 'Reversed': return 'This export was reversed. When the corrected payroll is final, generate a replacement in History.';
      case 'Superseded': return 'This export was replaced by a newer one. Open History to see it.';
      default: return 'This payroll already has an export in this mode. Download it again below; nothing new will be created.';
    }
  }
  if (!args.preview) return 'Preview the journal entry first. Export CSV is available once the preview is ready.';
  if (!previewIsExportable(args.preview)) return 'The preview is not ready to export. Fix what it lists, then preview again.';
  return null;
}

export function PayrollExportTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { caps } = useExportCapabilities();
  const runId = searchParams.get('runId') ?? '';
  const canGenerate = caps?.canGenerate !== false;

  const [scheduleId, setScheduleId] = useState('');
  const [list, setList] = useState<ListState>({ kind: 'loading' });
  const [reloadNonce, setReloadNonce] = useState(0);
  const [mode, setMode] = useState<ExportMode>('Compound');
  // What this person changed for this preview and export, on top of the company setting (null: use the company setting).
  const [overrides, setOverrides] = useState<OptionOverrides>(NO_OVERRIDES);
  const companySettings = useExportSettings();
  const [accountingDate, setAccountingDate] = useState('');
  const [preview, setPreview] = useState<PreviewState>({ kind: 'idle' });
  const [readiness, setReadiness] = useState<{ kind: 'loading' } | { kind: 'ready'; data: MappingReadiness } | { kind: 'unavailable' }>({ kind: 'loading' });
  const [runExports, setRunExports] = useState<ExportSummary[]>([]);
  const [busy, setBusy] = useState(false);
  const [writeDenied, setWriteDenied] = useState(false);
  const [result, setResult] = useState<ResultState | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The server refused the preview or the export because the plan does not include the accounting export (SUBSCRIPTION_UPGRADE_REQUIRED).
  const [planRefusal, setPlanRefusal] = useState<SubscriptionRestriction | null>(null);

  const summaryRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const focusSummary = useRef(false);
  const focusResult = useRef(false);

  const setRunId = useCallback((id: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('runId', id); else next.delete('runId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // ---- eligible payrolls ----
  useEffect(() => {
    const controller = new AbortController();
    accountingGateway
      .listEligiblePayrolls(scheduleId ? { payScheduleId: scheduleId } : undefined, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setList({ kind: 'ready', data }); })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setList(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: exportErrorMessage(err, 'Could not load the finalized payrolls.') });
      });
    return () => controller.abort();
  }, [scheduleId, reloadNonce]);

  const options: PayScheduleOption[] = list.kind === 'ready' ? list.data.payScheduleOptions : [];
  const run = list.kind === 'ready' ? list.data.items.find((r) => r.payrollRunId === runId) ?? null : null;

  // A selection that is no longer in the list (a different schedule) is cleared.
  useEffect(() => {
    if (list.kind === 'ready' && runId && !list.data.items.some((r) => r.payrollRunId === runId)) setRunId('');
  }, [list, runId, setRunId]);

  // ---- per selected payroll: mapping readiness and its existing exports ----
  useEffect(() => {
    if (!runId) return;
    const controller = new AbortController();
    accountingGateway.getMappingReadiness(runId, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setReadiness({ kind: 'ready', data }); })
      .catch(() => { if (!controller.signal.aborted) setReadiness({ kind: 'unavailable' }); });
    (async () => {
      const page = await accountingGateway.listExports({ runId }, controller.signal);
      // Listing does not look at the payroll; the status check does. An export that could still be out of date is checked, so a payroll corrected after it was
      // exported shows Reversal required here (people who cannot generate see the stored status).
      return Promise.all(page.items.map(async (e) => {
        if (!canGenerate || e.kind === 'Reversal' || (e.status !== 'Generated' && e.status !== 'Downloaded')) return e;
        try {
          const { lines, stateHistory, ...summary } = await accountingGateway.syncPayrollStatus(e.id);
          void lines; void stateHistory;
          return summary;
        } catch {
          return e;
        }
      }));
    })()
      .then((items) => { if (!controller.signal.aborted) setRunExports(items); })
      .catch(() => { if (!controller.signal.aborted) setRunExports([]); });
    return () => controller.abort();
  }, [runId, reloadNonce, canGenerate]);

  useEffect(() => {
    if (preview.kind === 'ready' && focusSummary.current) {
      focusSummary.current = false;
      summaryRef.current?.focus();
    }
  }, [preview]);
  useEffect(() => {
    if (result && focusResult.current) {
      focusResult.current = false;
      resultRef.current?.focus();
    }
  }, [result]);

  const perEmployeeAllowed = caps?.canViewPerEmployee !== false;
  const effectiveMode: ExportMode = perEmployeeAllowed ? mode : 'Compound';
  const optionInput = requestOptions({ mode: effectiveMode, overrides, settings: companySettings, perEmployeeAllowed });
  const dateValue = accountingDate || run?.checkDate || '';

  function resetOutput() {
    setPreview({ kind: 'idle' });
    setResult(null);
    setNotice(null);
    setPlanRefusal(null);
  }

  /** Shows the plan refusal (explanation, locked-feature dialog, Compare plans) instead of a generic error. Returns whether `err` was one. */
  function showPlanRefusal(err: unknown): boolean {
    const refusal = upgradeRequiredFromError(err);
    if (!refusal) return false;
    clearSubscriptionRestrictionNotice();
    setPlanRefusal(refusal);
    return true;
  }

  function chooseRun(id: string) {
    setReadiness({ kind: 'loading' });
    setRunExports([]);
    resetOutput();
    setAccountingDate('');
    setRunId(id);
  }

  async function runPreview() {
    if (!runId) return;
    setResult(null);
    setNotice(null);
    setPlanRefusal(null);
    setPreview({ kind: 'loading' });
    try {
      const data = await accountingGateway.previewExport({
        payrollRunId: runId, mode: effectiveMode, ...(accountingDate && accountingDate !== run?.checkDate ? { accountingDate } : {}), ...optionInput,
      });
      focusSummary.current = true;
      setPreview({ kind: 'ready', data });
    } catch (err) {
      if (showPlanRefusal(err)) {
        setPreview({ kind: 'idle' });
        return;
      }
      const message = hasCode(err, ExportErrorCode.PerEmployeeNotPermitted)
        ? 'Per-employee detail needs permission to view employee compensation. Use the compound entry, or ask a company administrator.'
        : exportErrorMessage(err, 'Could not preview this payroll.');
      setPreview({ kind: 'error', message });
    }
  }

  async function refreshAfterChange() {
    setReloadNonce((n) => n + 1);
  }

  async function generate() {
    if (!runId || preview.kind !== 'ready' || !previewIsExportable(preview.data)) return;
    setBusy(true);
    setNotice(null);
    setPlanRefusal(null);
    focusResult.current = true;
    try {
      const created = await accountingGateway.createExport({
        payrollRunId: runId, mode: effectiveMode, ...(accountingDate && accountingDate !== run?.checkDate ? { accountingDate } : {}), ...optionInput,
      });
      let fileName: string | null = null;
      let downloadError: string | null = null;
      try {
        const file = await accountingGateway.downloadExport(created.export.id);
        saveDownloadedExport(file);
        fileName = file.fileName;
      } catch (err) {
        downloadError = exportErrorMessage(err, 'The export was generated, but the file could not be downloaded.');
      }
      setResult({ kind: 'generated', export: created.export, duplicate: created.duplicate, fileName, downloadError });
      setPreview({ kind: 'idle' });
      await refreshAfterChange();
    } catch (err) {
      if (err instanceof ExportValidationError) {
        focusSummary.current = true;
        setPreview({ kind: 'ready', data: err.report });
        setResult({ kind: 'blocked', message: 'The server checked this export again and blocked it. Nothing was created. The reasons are listed in the preview.' });
      } else if (showPlanRefusal(err)) {
        // The plan, not the person's permission: nothing was created, and the preview they were looking at stays.
        focusResult.current = false;
      } else {
        if (isForbidden(err)) setWriteDenied(true);
        setResult({ kind: 'error', message: exportErrorMessage(err, 'The export could not be generated.') });
        await refreshAfterChange();
      }
    } finally {
      setBusy(false);
    }
  }

  async function downloadAgain(exp: ExportSummary) {
    setNotice(null);
    try {
      const file = await accountingGateway.downloadExport(exp.id);
      saveDownloadedExport(file);
      setNotice(`Downloaded ${file.fileName}.`);
      await refreshAfterChange();
    } catch (err) {
      setNotice(exportErrorMessage(err, 'The file could not be downloaded.'));
    }
  }

  const existing = useMemo(() => {
    const same = runExports.filter((e) => e.kind !== 'Reversal' && e.mode === effectiveMode && e.status !== 'Failed');
    return same.sort((a, b) => (a.generatedAtUtc < b.generatedAtUtc ? 1 : -1))[0] ?? null;
  }, [runExports, effectiveMode]);

  const reason = blockedReason({
    hasRun: !!runId && !!run, preview: preview.kind === 'ready' ? preview.data : null, canGenerate, writeDenied, busy, existing,
  });

  if (caps && !caps.canPreview) {
    return (
      <section className="ax-tab" aria-labelledby="ax-pe-title">
        <h3 id="ax-pe-title">Payroll export</h3>
        <AccessDeniedNotice requiredPermissionLabel="permission to view accounting exports" />
      </section>
    );
  }
  if (list.kind === 'forbidden') {
    return (
      <section className="ax-tab" aria-labelledby="ax-pe-title">
        <h3 id="ax-pe-title">Payroll export</h3>
        <AccessDeniedNotice requiredPermissionLabel="permission to view accounting exports" />
      </section>
    );
  }

  return (
    <section className="ax-tab" aria-labelledby="ax-pe-title">
      <h3 id="ax-pe-title">Payroll export</h3>
      <p className="muted ax-lead">
        Turn a finalized payroll into a balanced journal entry and download it as a CSV file for your accountant or accounting software.
        Only approved payrolls appear here. EPN never recalculates payroll, and nothing is sent to any accounting system.
      </p>

      {caps && !caps.canGenerate && (
        <p className="jr-readonly" role="note">You can preview journal entries, but generating an export needs a permission you do not have.</p>
      )}
      {notice && <p className="jr-notice" role="status">{notice}</p>}

      {list.kind === 'loading' && <p role="status" className="muted">Loading finalized payrolls…</p>}
      {list.kind === 'error' && <RetryableError message={list.message} onRetry={() => { setList({ kind: 'loading' }); setReloadNonce((n) => n + 1); }} />}

      {list.kind === 'ready' && list.data.items.length === 0 && !scheduleId && (
        <EmptyState
          message="There are no finalized payrolls to export yet. A payroll appears here once it has been approved in Payroll Review."
          action={<Link to="/payroll-review">Open Payroll Review</Link>}
        />
      )}

      {list.kind === 'ready' && (list.data.items.length > 0 || scheduleId) && (
        <>
          <form className="ax-selector" aria-label="Choose a payroll to export" onSubmit={(e) => e.preventDefault()}>
            <div className="jr-field">
              <label htmlFor="ax-schedule">Pay schedule</label>
              <select id="ax-schedule" value={scheduleId} onChange={(e) => { setScheduleId(e.target.value); resetOutput(); }}>
                <option value="">All pay schedules</option>
                {options.filter((o) => o.id !== null).map((o) => <option key={o.id} value={o.id ?? ''}>{o.label}</option>)}
              </select>
            </div>
            <div className="jr-field ax-field--wide">
              <label htmlFor="ax-run">Payroll (check date)</label>
              <select id="ax-run" value={run ? runId : ''} onChange={(e) => chooseRun(e.target.value)} aria-describedby="ax-run-hint">
                <option value="">Choose a finalized payroll</option>
                {list.data.items.map((r) => (
                  <option key={r.payrollRunId} value={r.payrollRunId}>
                    {formatDate(r.checkDate)}, {r.payScheduleLabel}, {r.employeeCount} employees, {exportStatusLabel(r.exportStatus)}
                  </option>
                ))}
              </select>
              <p id="ax-run-hint" className="jr-hint">Only approved payrolls are listed.</p>
            </div>
            <div className="jr-field">
              <span className="ax-label" id="ax-target-label">Target</span>
              <p className="ax-static" aria-labelledby="ax-target-label"><strong>CSV file (download)</strong></p>
              <p className="jr-hint">Future integrations (direct connections to accounting software) are not available yet.</p>
            </div>
            <fieldset className="jr-choice ax-mode">
              <legend>Export mode</legend>
              <label>
                <input type="radio" name="ax-mode" value="Compound" checked={effectiveMode === 'Compound'} onChange={() => { setMode('Compound'); resetOutput(); }} />
                <span>Compound entry <span className="jr-hint">(default)</span></span>
              </label>
              <label>
                <input
                  type="radio" name="ax-mode" value="PerEmployee" checked={effectiveMode === 'PerEmployee'} disabled={!perEmployeeAllowed}
                  aria-describedby={perEmployeeAllowed ? 'ax-mode-hint' : 'ax-mode-denied'}
                  onChange={() => { setMode('PerEmployee'); resetOutput(); }}
                />
                <span>Per employee</span>
              </label>
              <p id="ax-mode-hint" className="jr-hint">{MODE_COPY[effectiveMode]}</p>
              {!perEmployeeAllowed && (
                <p id="ax-mode-denied" className="jr-hint">Per-employee export is unavailable: it needs permission to view employee compensation. Ask a company administrator.</p>
              )}
            </fieldset>
            <ExportOptionsPanel
              mode={effectiveMode} settings={companySettings} caps={caps} overrides={overrides} perEmployeeAllowed={perEmployeeAllowed}
              onChange={(next) => { setOverrides(next); resetOutput(); }}
            />
            <div className="jr-field">
              <label htmlFor="ax-date">Accounting date</label>
              <input id="ax-date" type="date" value={dateValue} disabled={!run} aria-describedby="ax-date-hint" onChange={(e) => { setAccountingDate(e.target.value); resetOutput(); }} />
              <p id="ax-date-hint" className="jr-hint">The date the entry belongs to. It defaults to the check date.</p>
            </div>
          </form>

          {list.data.items.length === 0 && <EmptyState message="There are no finalized payrolls for this pay schedule." />}

          {run && (
            <>
              <dl className="jr-facts ax-run-facts" aria-label="Selected payroll">
                <div className="jr-facts__row"><dt>Pay period</dt><dd>{periodLabel(run.periodStart, run.periodEnd)}</dd></div>
                <div className="jr-facts__row"><dt>Check date</dt><dd>{formatDate(run.checkDate)}</dd></div>
                <div className="jr-facts__row"><dt>Employees</dt><dd>{run.employeeCount}</dd></div>
                <div className="jr-facts__row"><dt>Gross earnings</dt><dd>{formatCents(run.grossEarningsCents)}</dd></div>
                <div className="jr-facts__row"><dt>Net pay</dt><dd>{formatCents(run.netPayCents)}</dd></div>
                <div className="jr-facts__row"><dt>Export status</dt><dd><StatusBadge status={run.exportStatus} /></dd></div>
              </dl>

              <ReadinessPanel readiness={readiness} runId={runId} />

              {runExports.length > 0 && (
                <section className="ax-block" aria-labelledby="ax-prev-title" data-testid="run-exports">
                  <h4 id="ax-prev-title">Exports for this payroll</h4>
                  {runExports.some((e) => e.status === 'ReversalRequired') && (
                    <p className="jr-warning" role="note">
                      <span aria-hidden="true">⚠ </span>This payroll was corrected after it was exported, so the export must be reversed. Open History to generate a reversal artifact.
                    </p>
                  )}
                  <div className="jr-table-scroll" role="region" aria-label="Exports for this payroll" tabIndex={0}>
                    <table className="jr-table">
                      <caption className="ax-caption">Exports already generated for this payroll</caption>
                      <thead><tr><th scope="col">Journal reference</th><th scope="col">Kind</th><th scope="col">Mode</th><th scope="col">Status</th><th scope="col">Generated</th><th scope="col">Actions</th></tr></thead>
                      <tbody>
                        {runExports.map((e) => (
                          <tr key={e.id}>
                            <td>{e.journalReference}</td>
                            <td>{e.kind}</td>
                            <td>{exportModeLabels[e.mode]}</td>
                            <td><StatusBadge status={e.status} /></td>
                            <td>{formatDateTime(e.generatedAtUtc)}</td>
                            <td className="ax-row-actions">
                              {e.status !== 'Failed' && (
                                <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" onClick={() => void downloadAgain(e)} aria-label={`Download ${e.journalReference} again`}>Download again</button>
                              )}{' '}
                              <Link to={`/accounting?tab=history&exportId=${encodeURIComponent(e.id)}`} aria-label={`Open ${e.journalReference} in History`}>Open in History</Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              <div className="jr-actions ax-actions">
                <button type="button" className="jr-btn jr-btn--primary" onClick={() => void runPreview()} disabled={preview.kind === 'loading' || busy || (caps !== null && !caps.canPreview)}>
                  {preview.kind === 'loading' ? 'Previewing…' : 'Preview journal entry'}
                </button>
                <button
                  type="button" className="jr-btn jr-btn--primary" onClick={() => void generate()} disabled={reason !== null || busy}
                  aria-describedby={reason ? 'ax-export-reason' : undefined}
                >
                  {busy ? 'Generating…' : 'Export CSV'}
                </button>
              </div>
              {reason && <p id="ax-export-reason" className="jr-hint ax-reason" data-testid="export-reason">{reason}</p>}
            </>
          )}

          {planRefusal && <UpgradeRefusalNotice restriction={planRefusal} fallbackFeatureName="Accounting export (CSV)" />}
          {preview.kind === 'loading' && <p role="status" className="muted">Building the journal entry preview…</p>}
          {preview.kind === 'error' && <p className="jr-error jr-error--block" role="alert">{preview.message}</p>}

          {result && (
            <div ref={resultRef} tabIndex={-1} className="ax-result" data-testid="export-result" aria-live="polite">
              {result.kind === 'generated' && <GeneratedPanel result={result} onDownload={() => void downloadAgain(result.export)} />}
              {result.kind === 'blocked' && <p className="jr-error jr-error--block" role="alert">{result.message}</p>}
              {result.kind === 'error' && (
                <p className="jr-error jr-error--block" role="alert">
                  {result.message} <Link to="/accounting?tab=history">Open History</Link> to see whether a record was kept.
                </p>
              )}
            </div>
          )}

          {preview.kind === 'ready' && <ExportPreviewView preview={preview.data} runId={runId} summaryRef={summaryRef} />}
        </>
      )}
    </section>
  );
}

function ReadinessPanel({ readiness, runId }: { readiness: { kind: 'loading' } | { kind: 'ready'; data: MappingReadiness } | { kind: 'unavailable' }; runId: string }) {
  const glMappingUrl = `/accounting?tab=gl-mapping&runId=${encodeURIComponent(runId)}`;
  return (
    <section className="ax-block ax-readiness" aria-labelledby="ax-ready-title" data-testid="mapping-readiness">
      <h4 id="ax-ready-title">Mapping readiness</h4>
      {readiness.kind === 'loading' && <p role="status" className="muted">Checking GL mappings for this payroll…</p>}
      {readiness.kind === 'unavailable' && <p className="muted">GL mapping readiness could not be checked here. The preview will say whether anything is missing. <Link to={glMappingUrl}>Open GL Mapping</Link></p>}
      {readiness.kind === 'ready' && readiness.data.ready && (
        <p className="jr-ok"><span aria-hidden="true">✔ </span>Every payroll item in this payroll has a GL mapping. <Link to={glMappingUrl}>Review GL Mapping</Link></p>
      )}
      {readiness.kind === 'ready' && !readiness.data.ready && (
        <div className="jr-warning" role="note">
          <p><span aria-hidden="true">⚠ </span><strong>Some payroll items cannot be exported yet.</strong> Map them, then preview again.</p>
          <ul className="jr-plain-list">
            {[...readiness.data.missing, ...readiness.data.ambiguous].map((item, i) => (
              <li key={`${item.category}-${item.subcategory}-${item.payrollCode}-${i}`}>
                {unmappedLabel(item)}: {unmappedReasonLabel[item.reason] ?? item.reason} ({item.lineCount} line{item.lineCount === 1 ? '' : 's'}){' '}
                <Link to={fixMappingUrl(runId, item)} aria-label={`Fix the mapping for ${unmappedLabel(item)}`}>Fix</Link>
              </li>
            ))}
          </ul>
          <p><Link to={glMappingUrl}>Open GL Mapping</Link></p>
        </div>
      )}
    </section>
  );
}

function GeneratedPanel({ result, onDownload }: { result: Extract<ResultState, { kind: 'generated' }>; onDownload: () => void }) {
  const e = result.export;
  return (
    <section className="jr-notice jr-notice--ok ax-generated" aria-labelledby="ax-gen-title">
      <h4 id="ax-gen-title">{result.duplicate ? 'This export already existed' : 'Export generated'}</h4>
      {result.duplicate && <p>The same export had already been generated, so nothing new was created. Its file is the same.</p>}
      <dl className="jr-facts">
        <div className="jr-facts__row"><dt>Journal reference</dt><dd>{e.journalReference}</dd></div>
        <div className="jr-facts__row"><dt>Status</dt><dd><StatusBadge status={e.status} /></dd></div>
        <div className="jr-facts__row"><dt>Lines</dt><dd>{e.lineCount}</dd></div>
        <div className="jr-facts__row" data-testid="generated-options"><dt>Options recorded</dt><dd>{describeNetPay(e.options, e.mode)}; wages: {describeWage(e.options)}</dd></div>
        <div className="jr-facts__row"><dt>Total debits</dt><dd>{formatCents(e.totalDebitsCents)}</dd></div>
        <div className="jr-facts__row"><dt>Total credits</dt><dd>{formatCents(e.totalCreditsCents)}</dd></div>
        <div className="jr-facts__row ax-checksum"><dt>Checksum (SHA-256)</dt><dd><code data-testid="checksum">{e.checksum}</code></dd></div>
      </dl>
      {result.fileName && <p>The CSV file {result.fileName} was downloaded.</p>}
      {result.downloadError && <p className="jr-error" role="alert">{result.downloadError}</p>}
      <button type="button" className="jr-btn jr-btn--secondary" onClick={onDownload}>Download again</button>{' '}
      <Link to={`/accounting?tab=history&exportId=${encodeURIComponent(e.id)}`}>Open in History</Link>
    </section>
  );
}
